import React, { useState, useEffect, useMemo } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Box,
  Alert,
  Button,
  Container
} from '@mui/material';
import api from '../../utils/axiosConfig';
import useThemeStyles from '../../theme/useThemeStyles';

const AdminDashboard = () => {
  const styles = useThemeStyles();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalWeddings: 0,
    totalSuppliers: 0,
    activeUsers: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchDashboardData = async () => {
    try {
      setError(null);
      setLoading(true);
      
      const [statsRes, activityRes] = await Promise.all([
        api.get('/api/admin/stats'),
        api.get('/api/admin/recent-activity')
      ]);

      setStats(statsRes.data);
      setRecentActivity(activityRes.data);
      setRetryCount(0); // Reset retry count on success
    } catch (error) {
      setError('Failed to load dashboard data. Please try again.');

      // Implement exponential backoff for retries
      if (retryCount < 3) {
        const timeout = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
        }, timeout);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [retryCount]); // Re-fetch when retry count changes

  // Memoize the stats cards to prevent unnecessary re-renders
  const statsCards = useMemo(() => [
    { title: 'Total Users', value: stats.totalUsers },
    { title: 'Active Weddings', value: stats.totalWeddings },
    { title: 'Total Suppliers', value: stats.totalSuppliers },
    { title: 'Active Users', value: stats.activeUsers }
  ], [stats]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography component="h2" className="pagename" sx={styles.text.pagename}>
        Admin Dashboard
      </Typography>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert 
            severity="error" 
            action={
              <Button color="inherit" size="small" onClick={fetchDashboardData}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Stats Cards */}
        {statsCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {card.title}
                </Typography>
                <Typography variant="h3">
                  {card.value || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {/* Recent Activity */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            <List>
              {recentActivity.map((activity, index) => (
                <ListItem key={`${activity.type}-${activity.timestamp}-${index}`} divider={index < recentActivity.length - 1}>
                  <ListItemText
                    primary={activity.description}
                    secondary={new Date(activity.timestamp).toLocaleString()}
                  />
                </ListItem>
              ))}
              {recentActivity.length === 0 && (
                <ListItem>
                  <ListItemText primary="No recent activity" />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default React.memo(AdminDashboard); 